import React, { useState, useEffect, useRef } from 'react';

const ForestMode = ({ audioContext, isSoundEnabled }) => {
  // --- STATE ---
  const [plants, setPlants] = useState([]);
  const [fireflies, setFireflies] = useState([]);
  const [windForce, setWindForce] = useState(0);
  const [isWindActive, setIsWindActive] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [showControlsInfo, setShowControlsInfo] = useState(false);
  const [localAudioEnabled, setLocalAudioEnabled] = useState(isSoundEnabled);

  // Audio nodes refs
  const audioRefs = useRef({
    crickets: [],
    windNode: null,
    windGain: null,
    leavesNode: null,
    noiseBuffer: null,
    subOsc: null,
    subGain: null
  });

  // Mouse refs for loops
  const mouseRef = useRef({ x: 0, y: 0 });
  const windStartTimeRef = useRef(0);


  useEffect(() => {
    setLocalAudioEnabled(isSoundEnabled);
  }, [isSoundEnabled]);

  // Hint timer control
  const hintTimeoutRef = useRef();

  useEffect(() => {
    const startTimer = () => {
        hintTimeoutRef.current = setTimeout(() => {
            setShowHint(false);
        }, 6000);
    };

    startTimer();
    return () => clearTimeout(hintTimeoutRef.current);
  }, []); // Run on mount

  const onHintHover = () => clearTimeout(hintTimeoutRef.current);
  const onHintLeave = () => {
     hintTimeoutRef.current = setTimeout(() => setShowHint(false), 6000);
  };

  // --- AUDIO ENGINE ---
  useEffect(() => {
    if (!audioContext) return;
    
    // 1. Create Noise Buffer (Only once) if not exists
    if (!audioRefs.current.noiseBuffer) {
        const bufferSize = audioContext.sampleRate * 2; 
        const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
        }
        audioRefs.current.noiseBuffer = buffer;
    }
  }, [audioContext]);

  // Wind Audio Logic (Spawn on Demand)
  useEffect(() => {
      if (!audioContext || !localAudioEnabled) return;

      if (isWindActive) {
          // Restart context if suspended (crucial for some browsers)
          if (audioContext.state === 'suspended') {
             audioContext.resume().catch(e => console.error(e));
          }

          const now = audioContext.currentTime;
          const { noiseBuffer } = audioRefs.current;
          
          if (!noiseBuffer) return;

          // Clean previous if any (safety)
          if (audioRefs.current.activeWind) {
             const old = audioRefs.current.activeWind;
             try { old.windSrc.stop(); old.subOsc.stop(); } catch(e){}
          }
          
          // --- 1. WIND GENERATION ---
          const windSrc = audioContext.createBufferSource();
          windSrc.buffer = noiseBuffer;
          windSrc.loop = true;
          
          const windFilter = audioContext.createBiquadFilter();
          windFilter.type = 'lowpass';
          windFilter.frequency.setValueAtTime(200, now);
          windFilter.Q.value = 0;
          
          const windGain = audioContext.createGain();
          windGain.gain.setValueAtTime(0.001, now); // Start non-zero
          
          windSrc.connect(windFilter);
          windFilter.connect(windGain);
          windGain.connect(audioContext.destination);

          // --- 2. SUBWOOFER GENERATION ---
          const subOsc = audioContext.createOscillator();
          subOsc.type = 'sine';
          subOsc.frequency.setValueAtTime(60, now); // Audible Sub Bass
          
          const subGain = audioContext.createGain();
          subGain.gain.setValueAtTime(0.001, now);
          
          subOsc.connect(subGain);
          subGain.connect(audioContext.destination);

          // START
          windSrc.start(now);
          subOsc.start(now);
          
          // ENVELOPE UP
          windGain.gain.linearRampToValueAtTime(0.8, now + 0.5);
          windFilter.frequency.linearRampToValueAtTime(800, now + 2.0);
          windFilter.Q.linearRampToValueAtTime(5, now + 2.0);
          
          subGain.gain.linearRampToValueAtTime(0.6, now + 0.2);
          subOsc.frequency.linearRampToValueAtTime(45, now + 2.0); // Pitch drop

          // Store active reference
          audioRefs.current.activeWind = { windSrc, windGain, windFilter, subOsc, subGain };

      } else {
          // --- FADE OUT & STOP ---
          const active = audioRefs.current.activeWind;
          if (active) {
              const now = audioContext.currentTime;
              
              // Fade out wind
              active.windGain.gain.cancelScheduledValues(now);
              active.windGain.gain.setValueAtTime(active.windGain.gain.value, now);
              active.windGain.gain.linearRampToValueAtTime(0, now + 0.3);
              
              // Reduce filter
              active.windFilter.frequency.cancelScheduledValues(now);
              active.windFilter.frequency.setValueAtTime(active.windFilter.frequency.value, now);
              active.windFilter.frequency.linearRampToValueAtTime(200, now + 0.3);

              // Fade out sub
              active.subGain.gain.cancelScheduledValues(now);
              active.subGain.gain.setValueAtTime(active.subGain.gain.value, now);
              active.subGain.gain.linearRampToValueAtTime(0, now + 0.3);
              
              // Stop nodes shortly after fade
              setTimeout(() => {
                 try { active.windSrc.stop(); } catch(e){}
                 try { active.subOsc.stop(); } catch(e){}
              }, 350);
              
              audioRefs.current.activeWind = null;
          }
      }

      // Cleanup on unmount only if active
      return () => {
         if (audioRefs.current.activeWind && isWindActive) {
             try { 
                 audioRefs.current.activeWind.windSrc.stop(); 
                 audioRefs.current.activeWind.subOsc.stop();
             } catch(e){}
         }
      };

  }, [isWindActive, localAudioEnabled, audioContext]);

  // Helper for one-shot sounds
  const playSoundEffect = (type) => {
      if (!audioContext || !localAudioEnabled) return;
      if (audioContext.state === 'suspended') audioContext.resume();
      
      const now = audioContext.currentTime;
      const gain = audioContext.createGain();
      gain.connect(audioContext.destination);
      
      if (type === 'plant') {
          // LKM: "Pleasant", organic pop/bubble (Deep & Soft)
          // Using two sine waves for a "hollow" wood-like sound
          const osc1 = audioContext.createOscillator();
          const osc2 = audioContext.createOscillator();
          
          osc1.type = 'sine';
          osc2.type = 'sine';
          
          // Pentatonic scale notes for pleasantness (e.g., C4, D4, E4, G4, A4)
          // Randomly pick a note:
          const notes = [261.63, 293.66, 329.63, 392.00, 440.00];
          const baseFreq = notes[Math.floor(Math.random() * notes.length)];

          osc1.frequency.setValueAtTime(baseFreq, now);
          osc2.frequency.setValueAtTime(baseFreq * 2, now); // Octave up
          
          osc1.connect(gain);
          osc2.connect(gain);
          
          // Soft envelope
          gain.gain.setValueAtTime(0, now);
          gain.gain.linearRampToValueAtTime(0.15, now + 0.05); // Soft attack
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8); // Smooth release
          
          osc1.start(now);
          osc2.start(now);
          osc1.stop(now + 0.8);
          osc2.stop(now + 0.8);

      } else if (type === 'firefly') {
          // PKM: Ultra-soft, quiet, swelling pad (Deep & Ethereal)
          const osc1 = audioContext.createOscillator();
          const osc2 = audioContext.createOscillator();
          const osc3 = audioContext.createOscillator();
          
          // All sines for maximum smoothness (no buzz)
          osc1.type = 'sine';
          osc2.type = 'sine';
          osc3.type = 'sine';
          
          // Lower pitch to remove "squeak" (Mid-range warmth: ~440-520Hz)
          const base = 440 + Math.random() * 50; 
          
          osc1.frequency.setValueAtTime(base, now);
          osc2.frequency.setValueAtTime(base * 1.25, now); // Major 3rd
          osc3.frequency.setValueAtTime(base * 1.5, now); // 5th
          
          // Subtle detune for width
          osc1.detune.value = -10;
          osc2.detune.value = 10;
          
          osc1.connect(gain);
          osc2.connect(gain);
          osc3.connect(gain);
          
          // Very quiet, slow "breathing" envelope
          gain.gain.setValueAtTime(0, now);
          gain.gain.linearRampToValueAtTime(0.025, now + 0.6); // Very quiet peak (was 0.06), Slow attack
          gain.gain.linearRampToValueAtTime(0.015, now + 1.5); // Gentle sustain hold
          gain.gain.linearRampToValueAtTime(0.001, now + 3.5); // Long, undetectable fade out
          
          // Gentle LFO for movement (not flickering, just swaying)
          const lfo = audioContext.createOscillator();
          const lfoGain = audioContext.createGain();
          lfo.frequency.value = 2; // Slow sway
          lfoGain.gain.value = 0.005; 
          lfo.connect(lfoGain);
          lfoGain.connect(gain.gain);
          lfo.start(now);
          lfo.stop(now + 3.5);

          osc1.start(now);
          osc2.start(now);
          osc3.start(now);
          
          osc1.stop(now + 3.5);
          osc2.stop(now + 3.5);
          osc3.stop(now + 3.5);
      }
  };

  
  // Firefly Behavior & Plant Physics
  useEffect(() => {
    if (isWindActive) {
        windStartTimeRef.current = Date.now();
    }

      let animationFrame;
      const update = () => {
          setFireflies(prev => prev.map(ff => {
            // Noise / Wander
            const wanderX = (Math.random() - 0.5) * 0.5;
            const wanderY = (Math.random() - 0.5) * 0.5;
            
            // Mouse Repulsion (if Wind or just proximity)
            let dx = ff.x - mouseRef.current.x;
            let dy = ff.y - mouseRef.current.y;
            let dist = Math.sqrt(dx*dx + dy*dy);
            
            let forceX = 0;
            let forceY = 0;

            // Wind Force (blows everything right)
            if (isWindActive) {
                forceX += 2 + Math.random(); 
                forceY += (Math.random() - 0.5); 
            }

            // Soft avoidance of mouse
            if (dist < 150) {
                forceX += (dx / dist) * 0.5;
                forceY += (dy / dist) * 0.5;
            }

            const vx = (ff.vx + wanderX + forceX) * 0.95; // friction
            const vy = (ff.vy + wanderY + forceY) * 0.95;

            // Screen wrap
            let newX = ff.x + vx;
            let newY = ff.y + vy;
            
            if (newX > window.innerWidth + 20) newX = -20;
            if (newX < -20) newX = window.innerWidth + 20;
            if (newY > window.innerHeight + 20) newY = -20;
            if (newY < -20) newY = window.innerHeight + 20;

            return { ...ff, x: newX, y: newY, vx, vy };
          }));

          setWindForce(prev => isWindActive ? Math.min(prev + 0.02, 1) : Math.max(prev - 0.01, 0));

          // Update detached plants (Flying away)
          setPlants(prevPlants => {
              // Storm Trigger: 5 seconds of wind
              const isStorming = isWindActive && (Date.now() - windStartTimeRef.current > 5000);

              const hasDetached = prevPlants.some(p => p.detached);
              if (!isStorming && !hasDetached) return prevPlants;

              const screenWidth = window.innerWidth;
              
              return prevPlants.map(p => {
                  let isDetached = p.detached;
                  
                  // If storming, randomly detach attached plants (Except Aigul)
                  if (isStorming && !isDetached && p.type !== 'aigul') {
                      if (Math.random() < 0.02) { // 2% chance per frame to fly away
                         isDetached = true;
                      }
                  }

                  if (!isDetached) return p;

                  return {
                      ...p,
                      detached: true,
                      x: p.x + (15 + Math.random() * 10), // Fly right speed
                      y: p.y - (5 + Math.random() * 5),   // Fly up speed
                      rotation: (p.rotation || 0) + (5 + Math.random() * 5), // Tumbling
                      opacity: (p.opacity !== undefined ? p.opacity : 1) - 0.002 // Slow fade
                  };
              }).filter(p => p.x < screenWidth + 400 && p.y > -400); // Remove when far off screen
          });

          animationFrame = requestAnimationFrame(update);
      };
      animationFrame = requestAnimationFrame(update);
      return () => cancelAnimationFrame(animationFrame);
  }, [isWindActive]);

  // Use a stable ref for playSoundEffect to access in event handlers
  const playSoundRef = useRef(playSoundEffect);
  playSoundRef.current = playSoundEffect;

  // --- INTERACTION HANDLERS ---
  const handlersRef = useRef({});

  handlersRef.current = {
      handleMouseDown: (e) => {
          // LMB: Plant (RICH GENERATION)
          if (e.button === 0) {
             const types = ['fern', 'flower_exotic', 'mushroom', 'crystal_grass', 'alien_bloom', 'blue_vine', 'aigul'];
             // Make Aigul legendary (1% chance)
             let type = types[Math.floor(Math.random() * (types.length - 1))];
             if (Math.random() > 0.97) type = 'aigul';

             const hueBase = Math.random() * 360;
             
             // Generate consistent complex geometry data at spawn
             const config = {
                 type,
                 hue: hueBase,
                 scale: type === 'aigul' ? 0.9 + Math.random() * 0.3 : 0.6 + Math.random() * 0.8, // Aigul is tall
                 stemCurve: (Math.random() - 0.5) * 40,
                 petals: Math.floor(5 + Math.random() * 8),
                 leafCount: Math.floor(4 + Math.random() * 6),
                 height: type === 'aigul' ? 140 + Math.random() * 40 : 60 + Math.random() * 100,
                 // Pre-calculate random offsets for leaves/spots to avoid re-calc on render
                 details: Array.from({ length: 12 }).map(() => Math.random()), 
                 growthSpeed: 0.5 + Math.random() * 1.5
             };

             setPlants(prev => [...prev, {
                 id: Date.now() + Math.random(),
                 x: e.clientX,
                 y: e.clientY,
                 ...config,
                 swayOffset: Math.random() * Math.PI * 2
             }]);
             setShowHint(false);
             playSoundRef.current('plant');
          }

          // RMB: Spawn Fireflies
          if (e.button === 2) {
              const newFireflies = [];
              for(let i=0; i<5; i++) {
                  newFireflies.push({
                      id: Date.now() + i,
                      x: e.clientX + (Math.random()-0.5)*50,
                      y: e.clientY + (Math.random()-0.5)*50,
                      vx: (Math.random()-0.5) * 2,
                      vy: (Math.random()-0.5) * 2,
                      size: 2 + Math.random() * 3,
                      pulseSpeed: 0.05 + Math.random() * 0.1
                  });
              }
              setFireflies(prev => [...prev, ...newFireflies]);
              setShowHint(false);
              playSoundRef.current('firefly');
          }

          // MMB: Wind
          if (e.button === 1) {
              e.preventDefault();
              setIsWindActive(true);
              // Ensure audio context is running
              if (audioContext && audioContext.state === 'suspended') {
                 audioContext.resume().catch(e => console.error(e));
              }
          }
      },
      handleMouseUp: (e) => {
          if (e.button === 1) {
              setIsWindActive(false);
          }
      },
      handleMouseMove: (e) => {
          mouseRef.current = { x: e.clientX, y: e.clientY };
      }
  };

  useEffect(() => {
      const down = e => handlersRef.current.handleMouseDown(e);
      const up = e => handlersRef.current.handleMouseUp(e);
      const move = e => handlersRef.current.handleMouseMove(e);

      window.addEventListener('mousedown', down);
      window.addEventListener('mouseup', up);
      window.addEventListener('mousemove', move);

      return () => {
        window.removeEventListener('mousedown', down);
        window.removeEventListener('mouseup', up);
        window.removeEventListener('mousemove', move);
      };
  }, []); // Run ONCE at mount

  return (
      <div style={{
          position: 'fixed',
          inset: 0,
          overflow: 'hidden',
          zIndex: 99990,
          pointerEvents: 'none'
      }}>
          <style>
              {`
                  @keyframes sway {
                      0% { transform: rotate(0deg); }
                      25% { transform: rotate(5deg); }
                      75% { transform: rotate(-3deg); }
                      100% { transform: rotate(0deg); }
                  }
                  @keyframes glowPulse {
                      0%, 100% { opacity: 0.4; transform: scale(1); }
                      50% { opacity: 1; transform: scale(1.2); }
                  }
                  @keyframes hintFadeIn {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                  }
                  @keyframes popUp {
                    0% { opacity: 0; transform: scale(0); }
                    80% { transform: scale(1.1); }
                    100% { opacity: 1; transform: scale(1); }
                  }
              `}
          </style>

          {/* Trees Overlay removed as requested */}

          {/* Plants layer */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 99991, pointerEvents: 'none' }}>
            <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible' }}>
                <defs>
                    <filter id="glowPlant" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <linearGradient id="stemGrad" x1="0" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#0d1f12" />
                        <stop offset="100%" stopColor="#4a8f60" />
                    </linearGradient>
                    <radialGradient id="aigulGrad" cx="0.3" cy="0.3" r="0.8">
                        <stop offset="0%" stopColor="#ff8a65" /> {/* Light Orange Highlight */}
                        <stop offset="50%" stopColor="#e64a19" /> {/* Rich Red-Orange */}
                        <stop offset="100%" stopColor="#8c260d" /> {/* Dark Shadow */}
                    </radialGradient>
                </defs>
            </svg>
            
            {plants.map(p => {
                const windLean = windForce * 25; 
                // Uses rotation if flying/detached, otherwise normal sway
                const sway = p.detached ? (p.rotation || 0) : (windLean + Math.sin(Date.now()/1000 + p.swayOffset) * 5);
                const opacity = p.opacity !== undefined ? p.opacity : 1;
                
                return (
                    <div key={p.id} style={{
                        position: 'absolute',
                        left: p.x,
                        top: p.y,
                        transformOrigin: 'bottom center',
                        transform: `translate(-50%, -100%) rotate(${sway}deg) scale(${p.scale})`,
                        transition: p.detached ? 'none' : 'transform 0.1s linear', // Disable transition when flying for smooth physics
                        filter: 'drop-shadow(0 0 5px rgba(100,255,150,0.3))',
                        pointerEvents: 'none',
                        opacity: opacity // Apply fade out
                    }}>
                        <svg width="200" height="200" viewBox="0 0 100 200" style={{ 
                            overflow: 'visible', 
                            display: 'block',
                            transformOrigin: 'bottom center',
                            animation: 'popUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' 
                        }}>
                            
                            {/* ALIEN BLOOM */}
                            {p.type === 'alien_bloom' && (
                                <g transform="translate(50, 200)">
                                    {/* Stem */}
                                    <path d={`M0,0 Q${p.stemCurve}, -50 0, -${p.height}`} 
                                          fill="none" stroke={`hsl(${p.hue}, 40%, 30%)`} strokeWidth="3" strokeLinecap="round" />
                                    {/* Petals */}
                                    <g transform={`translate(0, -${p.height}) rotate(${Date.now()/50 % 360})`}>
                                        {Array.from({length: p.petals}).map((_, i) => (
                                            <ellipse key={i} cx="0" cy="-15" rx="4" ry="18" 
                                                fill={`hsla(${p.hue}, 80%, 60%, 0.8)`}
                                                transform={`rotate(${(360/p.petals)*i})`} />
                                        ))}
                                        <circle r="6" fill={`hsl(${p.hue+180}, 90%, 80%)`} filter="url(#glowPlant)" />
                                    </g>
                                </g>
                            )}

                            {/* FERN */}
                            {p.type === 'fern' && (
                                <g transform="translate(50, 200)">
                                     <path d={`M0,0 Q${p.stemCurve/2}, -${p.height/2} ${p.stemCurve}, -${p.height}`} 
                                          fill="none" stroke={`hsl(${100 + p.hue/5}, 50%, 40%)`} strokeWidth="2" strokeLinecap="round" />
                                     {Array.from({length: p.leafCount}).map((_, i) => {
                                         const yPos = -(p.height / p.leafCount) * (i + 1);
                                         // Use pre-calc details for variety
                                         const sideVar = p.details[i] || 0.5;
                                         return (
                                            <g key={i} transform={`translate(${p.stemCurve * ((i+1)/p.leafCount)}, ${yPos})`}>
                                                <path d={`M0,0 Q10,${sideVar*5 - 10} 25,${sideVar*5 - 5}`} fill="none" stroke={`hsl(${120 + p.hue/5}, 60%, 50%)`} strokeWidth="2" strokeLinecap="round"/>
                                                <path d={`M0,0 Q-10,${sideVar*5 - 10} -25,${sideVar*5 - 5}`} fill="none" stroke={`hsl(${120 + p.hue/5}, 60%, 50%)`} strokeWidth="2" strokeLinecap="round"/>
                                            </g>
                                         );
                                     })}
                                </g>
                            )}

                            {/* MUSHROOM */}
                            {p.type === 'mushroom' && (
                                <g transform="translate(50, 200)">
                                    <path d={`M-6,0 L-4,-${p.height/2} Q0,-${p.height/2 + 5} 4,-${p.height/2} L6,0 Z`} fill={`hsl(${p.hue/2}, 20%, 30%)`} />
                                    <path d={`M-25,-${p.height/2} Q0,-${p.height} 25,-${p.height/2} Z`} fill={`hsl(${p.hue}, 70%, 50%)`} filter="url(#glowPlant)" />
                                    {/* Random Spots */}
                                    {p.details.slice(0,3).map((v, i) => (
                                        <circle key={i} cx={(v-0.5)*30} cy={-(p.height/2 + 5 + v*10)} r={1 + v*2} fill="white" opacity="0.6" />
                                    ))}
                                </g>
                            )}
                            
                            {/* CRYSTAL GRASS */}
                            {p.type === 'crystal_grass' && (
                                <g transform="translate(50, 200)">
                                    {/* Multiple sharp blades */}
                                    <path d={`M0,0 L-5,-${p.height*0.4} L0,-${p.height*0.8} L5,-${p.height*0.5} Z`} fill={`hsla(${p.hue}, 60%, 80%, 0.4)`} stroke="white" strokeWidth="0.5" />
                                    <path d={`M0,0 L12,-${p.height*0.3} L18,-${p.height*0.7} L6,-${p.height*0.4} Z`} fill={`hsla(${p.hue+20}, 60%, 80%, 0.4)`} stroke="white" strokeWidth="0.5" />
                                    <path d={`M0,0 L-12,-${p.height*0.35} L-15,-${p.height*0.6} L-4,-${p.height*0.4} Z`} fill={`hsla(${p.hue-20}, 60%, 80%, 0.4)`} stroke="white" strokeWidth="0.5" />
                                </g>
                            )}

                             {/* FLOWER EXOTIC */}
                             {p.type === 'flower_exotic' && (
                                <g transform="translate(50, 200)">
                                    <path d={`M0,0 Q${p.stemCurve/3}, -50 0, -${p.height}`} 
                                          fill="none" stroke="#6ca" strokeWidth="2" strokeLinecap="round" />
                                    <g transform={`translate(0, -${p.height})`}>
                                        <circle r="8" fill={`hsl(${p.hue}, 80%, 20%)`} />
                                        {Array.from({length: 8}).map((_, i) => (
                                           <g key={i} transform={`rotate(${i * 45})`}>
                                               <path d="M0,0 Q5,-15 0,-30 Q-5,-15 0,0" fill={`hsla(${p.hue}, 80%, 70%, 0.8)`} />
                                           </g>
                                        ))}
                                         <circle r="3" fill="#fff" filter="url(#glowPlant)" />
                                    </g>
                                </g>
                            )}

                             {/* BLUE VINE */}
                             {p.type === 'blue_vine' && (
                                <g transform="translate(50, 200)">
                                    <path d={`M0,0 C${p.stemCurve},-30 ${-p.stemCurve},-60 0,-100`} 
                                          fill="none" stroke={`hsl(${p.hue}, 70%, 60%)`} strokeWidth="3" strokeDasharray="4,2" />
                                    <circle cx="0" cy="-100" r="5" fill={`hsl(${p.hue+90}, 100%, 80%)`} filter="url(#glowPlant)" />
                                </g>
                             )}

                             {/* AIGUL (MOON FLOWER) - REALISTIC */}
                             {p.type === 'aigul' && (
                                <g transform="translate(50, 200)">
                                    {/* Stem - Thick and sturdy */}
                                    <path d={`M0,0 Q${p.stemCurve * 0.2},-${p.height * 0.5} 0,-${p.height}`} 
                                          fill="none" stroke="url(#stemGrad)" strokeWidth="6" strokeLinecap="round" />
                                    
                                    {/* Basal Leaves - Spread out at bottom */}
                                    {[1, -1].map((side, i) => (
                                       <path key={`bl-${i}`} 
                                             d={`M0,0 Q${side*25},-15 ${side*45},-5`} 
                                             stroke="#1b5e20" strokeWidth="4" fill="none" strokeLinecap="round" 
                                       />
                                    ))}

                                    {/* FLOWER CLUSTER - Umbel at top (hanging down) */}
                                    <g transform={`translate(0, -${p.height})`}>
                                        
                                        {/* Main Flowers (Spread Cluster) */}
                                        {[-50, -25, 0, 25, 50].map((angle, i) => (
                                            <g key={`fb-${i}`} transform={`translate(0, 5) rotate(${angle})`}>
                                                 {/* Pedicel - Arched outwards */}
                                                 <path d="M0,0 Q0,12 0,25" stroke="#2e7d32" strokeWidth="2" fill="none"/>
                                                 
                                                 {/* Bell Flower - Hanging at end of pedicel */}
                                                 {/* Rotate negative angle * 0.2 keeps them pointing slightly outwards but down */}
                                                 <g transform={`translate(0, 25) rotate(${-angle * 0.2}) scale(0.65)`}>
                                                    {/* Petals - Deep cuts for distinct petals */}
                                                    <path d="M0,0 C-12,15 -20,35 -24,55 L-8,45 L0,58 L8,45 L24,55 C20,35 12,15 0,0" fill="#e64a19" />
                                                    <path d="M0,0 C-12,15 -20,35 -24,55 L-8,45 L0,58 L8,45 L24,55 C20,35 12,15 0,0" fill="url(#aigulGrad)" />
                                                    
                                                    {/* Stamens - Long and visible */}
                                                    <line x1="-5" y1="35" x2="-8" y2="65" stroke="#ffeb3b" strokeWidth="1.5" />
                                                    <line x1="5" y1="35" x2="8" y2="65" stroke="#ffeb3b" strokeWidth="1.5" />
                                                    <line x1="0" y1="38" x2="0" y2="70" stroke="#ffeb3b" strokeWidth="2" />
                                                    
                                                    {/* Glow effect */}
                                                    <circle cx="0" cy="50" r="10" fill="#ffeb3b" opacity="0.4" filter="url(#glowPlant)" />
                                                 </g>
                                            </g>
                                        ))}

                                        {/* Top Crown of Leaves (Tuft) */}
                                        <g transform="translate(0, -2)">
                                           {Array.from({length: 10}).map((_, i) => {
                                               const angle = (i-4.5) * 15;
                                               return (
                                                    <path key={`t-${i}`} 
                                                        transform={`rotate(${angle})`}
                                                        d="M0,0 Q0,-25 0,-45" 
                                                        stroke="#43a047" strokeWidth="2.5" fill="none" strokeLinecap="round"
                                                    />
                                               )
                                           })}
                                        </g>
                                    </g>
                                </g>
                             )}

                        </svg>
                    </div>
                );
            })}
          </div>

          {/* Fireflies layer */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 99992, pointerEvents: 'none' }}>
            {fireflies.map(ff => (
                <div key={ff.id} style={{
                    position: 'absolute',
                    left: ff.x,
                    top: ff.y,
                    width: ff.size,
                    height: ff.size,
                    background: '#ccff00',
                    borderRadius: '50%',
                    boxShadow: '0 0 8px #ccff00',
                    opacity: 0.6 + Math.sin(Date.now() * 0.005 * ff.pulseSpeed) * 0.4,
                }} />
            ))}
          </div>

          {/* Initial Hint */}
          {showHint && (
               <div 
                   onMouseEnter={onHintHover}
                   onMouseLeave={onHintLeave}
                   style={{
                   position: 'fixed',
                   bottom: '40px',
                   right: '40px',
                   background: 'rgba(5, 20, 10, 0.75)',
                   backdropFilter: 'blur(20px) saturate(140%)',
                   padding: '32px 40px',
                   borderRadius: '24px',
                   border: '1px solid rgba(255, 255, 255, 0.08)',
                   boxShadow: '0 20px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)',
                   color: '#fff',
                   textAlign: 'left', // Align left for better reading of long text
                   pointerEvents: 'auto', // Enable hover
                   zIndex: 99995,
                   animation: 'hintFadeIn 1s cubic-bezier(0.16, 1, 0.3, 1)',
                   fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                   maxWidth: '400px'
               }}>
                    {/* Corner Accent */}
                    <div style={{
                       position: 'absolute', top: 0, right: 0, 
                       width: '60px', height: '60px',
                       background: 'radial-gradient(circle at top right, rgba(255,100,0,0.2), transparent 70%)',
                       borderRadius: '0 24px 0 0',
                       pointerEvents: 'none'
                   }}/>

                   <h2 style={{ 
                       fontSize: '28px', 
                       fontWeight: '300',
                       letterSpacing: '0.05em',
                       margin: '0 0 8px 0',
                       textTransform: 'uppercase',
                       color: '#fff',
                       display: 'flex', alignItems: 'center', gap: '12px'
                   }}>
                       <span style={{ fontSize: '24px' }}>🌿</span> Ночной Лес
                   </h2>

                   <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '20px', letterSpacing: '0.02em' }}>
                     Магия природы оживает от прикосновений
                   </div>
                   
                   <div style={{ 
                       width: '100%', height: '1px', 
                       background: 'rgba(255,255,255,0.1)', 
                       margin: '0 0 20px 0' 
                   }} />

                   <div style={{ marginBottom: '20px' }}>
                       <div style={{ 
                           fontSize: '11px', 
                           textTransform: 'uppercase', 
                           color: '#ff8c00', // Legendary Orange
                           fontWeight: '700', 
                           letterSpacing: '0.1em',
                           marginBottom: '6px'
                       }}>
                           ЛЕГЕНДАРНЫЙ (5%)
                       </div>
                       <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '500', color: '#ffccaa' }}>
                           Цветок Айгуль
                       </h3>
                       <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: 'rgba(255,255,255,0.85)', textAlign: 'justify' }}>
                           Айгуль («Лунный цветок») — гордость кыргызских гор и символ стойкости. Этот легендарный цветок растет лишь на каменистых склонах Баткена. В нашем цифровом лесу он расцветает редко, даря свою красоту самым терпеливым.
                       </p>
                   </div>
               </div>
          )}


          {/* Controls UI (Info Button & Modal) - REMOVED */}

      </div>
  );
};

export default ForestMode;
